import { Middleware, MiddlewareAPI, Dispatch, AnyAction } from 'redux'
import { RootState } from 'client/store'
import { ReduxLoggingTypeEnum } from 'src/editor/store/redux-logging/redux-logging-reducer'
import {
  addReduxLog,
  resetAllReduxLogData,
  sendReduxLogsToRollbar,
  setReduxLogData,
} from 'src/editor/store/redux-logging/redux-logging-actions'
import { PageEntityInterface } from 'src/shared/types/base-entity-interface'
import { MasterBlockIdType } from 'src/shared/schemas/master-block-id.schema'
import { getEntitiesGroupRoot } from 'common/utils/entityUtils'
import { REDUX_LOGGING_ACTIONS_TYPES } from 'src/editor/store/redux-logging/redux-logging-action-types'
import { UPDATE_PAGE_FROM_DB } from '../page/pageActionTypes'
import {
  findAllMasterBlocksIds,
  findMasterBlockEntities,
} from 'common/utils/entityUtils'

/**
 * NOTE: This middleware is used to log redux
 *
 * ------ ReduxLoggingTypeEnum.ROOT_MASTER_BLOCK_ENTITIES_LOST_MASTER_BLOCK_ID logging ---------
 * The middleware sends error to Rollbar if the root entity of a master block has lost the masterBlockId during the page editing
 *
 * Examples:
 * 1. The root entity of a master block has isMasterBlockRoot
 * Initial state:
 * ```
 * entities: {
 *   entity1: {
 *     masterBlockId: 1,
 *     isMasterBlockRoot: true,
 *     ...
 *   },
 *   entity2: {
 *     masterBlockId: 1,
 *     ...
 *   }
 * }
 * ```
 * After page editing:
 * ```
 * entities: {
 *   entity1: {
 *     isMasterBlockRoot: true,
 *     ...
 *   },
 *   entity2: {
 *     masterBlockId: 1,
 *     ...
 *   }
 * }
 * ```
 * Result: it will be sent to Rollbar
 *
 * 2. The root entity of a master block doesn't have isMasterBlockRoot=true
 * Initial state:
 * ```
 * entities: {
 *   entity1: {
 *     masterBlockId: 1,
 *     ...
 *   },
 *   entity2: {
 *     masterBlockId: 1,
 *     ...
 *   }
 * }
 * ```
 * After page editing:
 * ```
 * entities: {
 *   entity1: {
 *     ...
 *   },
 *   entity2: {
 *     masterBlockId: 1,
 *     ...
 *   }
 * }
 * ```
 * Result: it will be sent to Rollbar
 *
 * 3. The root entity of a master block didn't have masterBlockId initially
 * Initial state:
 * ```
 * entities: {
 *   entity1: {
 *     masterBlockId: 1,
 *     isMasterBlockRoot: true,
 *     ...
 *   },
 *   entity2: {
 *     masterBlockId: 1,
 *     ...
 *   }
 * }
 * ```
 * Result: it won't be sent to Rollbar
 * ----------------------------------------------------------------------------
 */

// @ts-expect-error we will fix it while migrating to the redux toolkit
export const logRootMasterBlockEntitiesLostMasterBlockIdMiddleware: Middleware<
  Dispatch<AnyAction>,
  RootState
> =
  (storeApi: MiddlewareAPI<Dispatch<AnyAction>, RootState>) =>
  (next: Dispatch<AnyAction>) =>
  (action: AnyAction) => {
    // NOTE: Get the current state from the store
    const state = storeApi.getState()

    // NOTE: Extract the current page and its entities from the state
    const page = state.page.present
    const entities = page.entities

    // NOTE: Only process actions that are not redux logging actions to avoid infinite loops
    if (!REDUX_LOGGING_ACTIONS_TYPES.includes(action.type as any)) {
      // NOTE: Find all unique master block IDs in the current entities
      const masterBlocksIds = findAllMasterBlocksIds(entities)

      masterBlocksIds.forEach(masterBlockId => {
        // NOTE: Get all entities that belong to this specific master block
        const masterBlockEntities = findMasterBlockEntities(
          entities,
          masterBlockId,
        )

        // NOTE: Find the root entity of this master block
        const rootMasterBlockEntity = getEntitiesGroupRoot(masterBlockEntities)

        if (rootMasterBlockEntity) {
          // NOTE: Check if we already have logging data for this master block
          const masterBlockLoggingType =
            state.reduxLogging.logData[
              ReduxLoggingTypeEnum
                .ROOT_MASTER_BLOCK_ENTITIES_LOST_MASTER_BLOCK_ID
            ]?.[masterBlockId as MasterBlockIdType]

          // NOTE: If no logging data exists for this master block, create initial error data
          if (!masterBlockLoggingType) {
            storeApi.dispatch(
              setReduxLogData({
                logType:
                  ReduxLoggingTypeEnum.ROOT_MASTER_BLOCK_ENTITIES_LOST_MASTER_BLOCK_ID,
                data: {
                  masterBlockId: masterBlockId as MasterBlockIdType,
                  rootEntity: rootMasterBlockEntity as PageEntityInterface,
                },
              }),
            )
          }
          // NOTE: If the root entity has been changed (it happens because the masterBlockId has been removed)
          else if (
            Object.keys(masterBlockEntities).length && // NOTE: If the master block is existed,
            !masterBlockLoggingType?.shouldBeSentToRollbar && // NOTE: If the log has not been sent to Rollbar yet
            masterBlockLoggingType?.rootEntity?.id !== rootMasterBlockEntity.id // NOTE: If the root entity has been changed
          ) {
            storeApi.dispatch(
              sendReduxLogsToRollbar({
                logType:
                  ReduxLoggingTypeEnum.ROOT_MASTER_BLOCK_ENTITIES_LOST_MASTER_BLOCK_ID,
                data: {
                  masterBlockId: masterBlockId as MasterBlockIdType,
                },
              }),
            )
          }
        }
      })

      // NOTE: Reset logging data when page is updated from database logs
      if (action.type === UPDATE_PAGE_FROM_DB) {
        storeApi.dispatch(
          resetAllReduxLogData({
            logType:
              ReduxLoggingTypeEnum.ROOT_MASTER_BLOCK_ENTITIES_LOST_MASTER_BLOCK_ID,
          }),
        )
      }

      // NOTE: Log all non-redux-logging actions for debugging purposes
      storeApi.dispatch(
        addReduxLog({
          newLog: JSON.stringify(action),
        }),
      )
    }

    return next(action as AnyAction)
  }
